/**
 * Global unread-notes indicator, rendered in the Header.
 *
 * The Team Chat tab and the landing card each carry their own badge,
 * but both are only visible once you're already on that screen. A note
 * arriving while someone works in Depletions or Admin would otherwise
 * go unseen until they happened to navigate home. This puts the signal
 * on every page that uses SiteLayout.
 *
 * Renders ONLY when there's unread mail (or while you're on the notes
 * page). A always-present icon with no badge reads as an unexplained
 * glyph beside the brand mark rather than as "no new messages" — the
 * landing card and the rep tab are the standing ways in, so this is
 * purely an interrupt.
 *
 * The count comes from the same polled query the other two badges use,
 * so TanStack dedupes it to a single request no matter how many
 * consumers are mounted.
 */

import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { Badge, IconButton, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUnreadNoteCount } from '../api/notes';
import { useAuth } from '../auth/useAuth';

const GOLD = '#bb8c3f';

export function NotesBell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Signed-out shells (login, reset-password) render the Header too —
  // don't poll an endpoint that would 401.
  const enabled = !!user;
  const unread = useUnreadNoteCount(enabled).data?.unread_count ?? 0;

  if (!user) return null;

  // Nothing to report, nothing to show. A permanently-present icon with
  // no badge is an unexplained glyph next to the brand mark — people
  // ask what it is rather than reading it as "no new messages". The
  // landing card and the rep tab are the standing entry points; this is
  // purely an interrupt, so it exists only when it has something to
  // interrupt for.
  //
  // Nor on the notes page itself, where a shortcut to the page you're
  // already reading is noise regardless of the count.
  const onNotesPage = location.pathname.startsWith('/notes');
  if (unread === 0 || onNotesPage) return null;

  const label = `Team Chat — ${unread} unread message${unread === 1 ? '' : 's'}`;

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={() => navigate('/notes')}
        aria-label={label}
        size="small"
        sx={{
          mr: { xs: 0.25, sm: 0.75 },
          // Always gold now — the component only renders when there's
          // unread mail, so the low-contrast "resting" state is dead.
          color: GOLD,
          transition: 'color 200ms ease',
          '&:hover': { color: GOLD, bgcolor: 'rgba(187, 140, 63, 0.08)' },
        }}
      >
        <Badge
          badgeContent={unread}
          max={99}
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: GOLD,
              color: '#fff',
              fontFamily: '"Inter", sans-serif',
              fontSize: 10,
              fontWeight: 700,
              minWidth: 16,
              height: 16,
              px: 0.4,
            },
          }}
        >
          <ForumOutlinedIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
